import { authHeader } from '@/_helpers'

export const agentsService = {
  getAgentConfig,
  updateAgentPrompt,
  createAgentPrompt,
  getElevenLabsAgent,
  generateClientPrompt,
}

const apiUrl = process.env.VUE_APP_ROOT_API

/**
 * Recupera la configuración del agente IA de un cliente.
 * Devuelve `null` cuando el backend responde 404 (cliente sin agente todavía).
 */
function getAgentConfig(clientId) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader('application/json'),
  }
  return fetch(
    `${apiUrl}/admin/clients/${clientId}/agent`,
    requestOptions,
  ).then((response) => {
    if (response.status === 404) return null
    return handleResponse(response)
  })
}

function buildAgentPayload({
  prompt,
  isEnabled,
  maxConcurrentCalls,
  elevenlabsAgentId,
  elevenlabPhoneId,
}) {
  const payload = {
    prompt,
    is_enabled: isEnabled ? 'yes' : 'no',
  }
  if (maxConcurrentCalls !== undefined && maxConcurrentCalls !== null) {
    payload.max_concurrent_calls = String(maxConcurrentCalls)
  }
  if (elevenlabsAgentId !== undefined && elevenlabsAgentId !== null) {
    payload.elevenlabs_agent_id = String(elevenlabsAgentId).trim()
  }
  // El backend persiste este id en la columna `elevenlab_phone_id`. Se obtiene
  // desde `elevenlab_agent.phone_numbers[0].phone_number_id` en el front, por lo
  // que puede llegar vacío si el agente todavía no tiene un teléfono asignado
  // en ElevenLabs.
  if (elevenlabPhoneId !== undefined && elevenlabPhoneId !== null) {
    payload.elevenlab_phone_id = String(elevenlabPhoneId).trim()
  }
  return payload
}

/**
 * Actualiza el prompt del agente IA y su estado (activo/inactivo).
 * El backend espera `is_enabled` como string 'yes' o 'no'.
 * `max_concurrent_calls` se envía como string para mantener el formato del backend.
 * `elevenlabs_agent_id` se envía como string (vacío si se quiere limpiar el valor).
 * `elevenlab_phone_id` se envía como string (vacío si el agente no tiene teléfono
 * configurado todavía en ElevenLabs).
 */
function updateAgentPrompt(clientId, params) {
  const requestOptions = {
    method: 'PUT',
    headers: authHeader('application/json'),
    body: JSON.stringify(buildAgentPayload(params)),
  }
  return fetch(
    `${apiUrl}/admin/clients/${clientId}/agent/prompt`,
    requestOptions,
  ).then(handleResponse)
}

/**
 * Crea por primera vez la configuración del agente IA para un cliente.
 * El endpoint es INSERT puro: si ya existe, devuelve 409.
 */
function createAgentPrompt(clientId, params) {
  const requestOptions = {
    method: 'POST',
    headers: authHeader('application/json'),
    body: JSON.stringify(buildAgentPayload(params)),
  }
  return fetch(
    `${apiUrl}/admin/clients/${clientId}/agent/prompt`,
    requestOptions,
  ).then(handleResponse)
}

/**
 * Recupera la info de un agente de ElevenLabs por su `agent_id` vía backend.
 * Se usa para verificar, antes de crear la configuración local, que el agente
 * realmente existe en ElevenLabs y que tiene un número de teléfono asignado.
 * Devuelve `null` cuando el backend responde 404 o un cuerpo nulo (no se encontró el agente).
 */
function getElevenLabsAgent(agentId) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader('application/json'),
  }
  return fetch(
    `${apiUrl}/admin/get-elevenlabs-agent/${encodeURIComponent(agentId)}`,
    requestOptions,
  ).then((response) => {
    if (response.status === 404) return null
    return handleResponse(response)
  })
}

/**
 * Genera automáticamente, vía IA, el prompt del agente para un cliente.
 * El backend responde con el prompt generado (string o estructura por bloques).
 */
function generateClientPrompt(clientId) {
  const requestOptions = {
    method: 'POST',
    headers: authHeader('application/json'),
    body: JSON.stringify({ client_id: Number(clientId) }),
  }
  return fetch(
    `${apiUrl}/admin/generate-client-prompt`,
    requestOptions,
  ).then(handleResponse)
}

function handleResponse(response) {
  return response.text().then((text) => {
    const data = text && JSON.parse(text)
    if (!response.ok) {
      const base = data && typeof data === 'object' ? data : { message: data || response.statusText }
      const error = { ...base, status: response.status }
      return Promise.reject(error)
    }
    return data
  })
}
